import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import './TCCWhitePaper.scss';
import MainHeader from "@/components/MainHeader"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { theme } from "./theme"
import TCCWHITEPAPER from "../../assets/TCC Whitepaper 2025_Final.pdf";

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F9FA',
  textAlign: 'center',
  padding: '80px 0',
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '3.5rem',
  fontWeight: 700,
  color: '#3257B8',
  marginBottom: '0.5rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
}));

const HeroTagline = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '1.5rem',
  color: '#3257B8',
  fontWeight: 600,
  marginBottom: '1.5rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
  },
}));

const HeroSummary = styled(Typography)(({ theme }) => ({
  maxWidth: '750px',
  margin: '0 auto 2.5rem auto',
  fontSize: '1.1rem',
  lineHeight: 1.7,
  color: '#212529',
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3257B8',
  color: '#FFFFFF',
  padding: '15px 35px',
  borderRadius: '50px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '1.1rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#284493',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const PillarsSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  backgroundColor: '#FFFFFF',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#3257B8',
  textAlign: 'center',
  marginBottom: '1rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  maxWidth: '700px',
  margin: '0 auto 4rem auto',
  fontSize: '1.1rem',
  color: '#6c757d',
  lineHeight: 1.7,
}));

const PillarCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '40px',
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  boxShadow: '0 5px 25px rgba(0, 0, 0, 0.08)',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
  },
}));

const PillarIcon = styled(Box)(({ theme }) => ({
  fontSize: '3rem',
  color: '#073aeb',
  marginBottom: '1.5rem',
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
  },
}));

const PillarTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#1B263B',
  marginBottom: '1rem',
}));

const FinalCTASection = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F9FA',
  padding: '80px 0',
  textAlign: 'center',
}));

const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#2F54D3',
  color: '#E0E1DD',
  padding: '60px 0 0 0',
}));

const FooterGrid = styled(Grid)(({ theme }) => ({
  marginBottom: '40px',
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#FFFFFF',
  marginBottom: '20px',
  fontWeight: 600,
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: '#E0E1DD',
  fontSize: '0.9rem',
  lineHeight: 1.6,
}));

const FooterLink = styled('a')(({ theme }) => ({
  color: '#E0E1DD',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: '#FFFFFF',
  },
}));

const SocialIcon = styled('a')(({ theme }) => ({
  display: 'inline-block',
  height: '40px',
  width: '40px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  marginRight: '10px',
  textAlign: 'center',
  lineHeight: '40px',
  borderRadius: '50%',
  color: '#FFFFFF',
  transition: 'background-color 0.3s ease, color 0.3s ease',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    color: '#2F54D3',
  },
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  padding: '20px 0',
  textAlign: 'center',
  fontSize: '0.9rem',
  color: '#E0E1DD',
}));

const TCCWhitePaper: React.FC = () => {
  const handleDownload = () => {
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = TCCWHITEPAPER;
    link.download = 'TCC_Whitepaper_2025.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainHeader />
      <Box className="tcc-whitepaper" sx={{ 
        // Remove any default margins/padding that might interfere with main layout
        margin: 0,
        padding: 0,
        width: '100%',
        // Override any container constraints from main layout
        maxWidth: 'none !important',
        '& .MuiContainer-root': {
          maxWidth: 'none !important',
          padding: '0 30px !important',
        }
      }}>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <HeroTitle variant="h1">
              The Educator's Compass
            </HeroTitle>
            <HeroTagline>
              A Strategic Framework for the Future of AI in Education
            </HeroTagline>
            <HeroSummary>
              Synthesized from proposals by frontline educators at TCC 2025, this white paper presents a strategic framework for integrating AI into education. It provides a clear, actionable blueprint for transforming AI from a potential risk into a powerful, equitable tool for deep, human-centered learning.
            </HeroSummary>
            <CTAButton onClick={handleDownload}>
              Download the Full White Paper (PDF)
            </CTAButton>
          </Container>
        </HeroSection>

        {/* Pillars Section */}
        <PillarsSection>
          <Container maxWidth="lg">
            <SectionTitle variant="h2">
              Three Pillars of Educational Transformation
            </SectionTitle>
            <SectionSubtitle>
              Our comprehensive strategy is built upon three interconnected pillars, addressing the critical challenges and opportunities of AI in the modern classroom.
            </SectionSubtitle>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <PillarCard>
                  <CardContent>
                    <PillarIcon>
                      <LightbulbIcon />
                    </PillarIcon>
                    <PillarTitle>
                      Pillar 1: Pedagogy & Empowerment
                    </PillarTitle>
                    <Typography>
                      Moving students from passive content consumers to active, critical creators by blending inquiry-based learning with innovative AI tools and a focus on process over product.
                    </Typography>
                  </CardContent>
                </PillarCard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <PillarCard>
                  <CardContent>
                    <PillarIcon>
                      <SecurityIcon />
                    </PillarIcon>
                    <PillarTitle>
                      Pillar 2: Integrity & Ethics
                    </PillarTitle>
                    <Typography>
                      Cultivating a culture of responsible digital citizenship through a school-wide AI Code of Conduct and a shift towards process-based assessment.
                    </Typography>
                  </CardContent>
                </PillarCard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <PillarCard>
                  <CardContent>
                    <PillarIcon>
                      <PublicIcon />
                    </PillarIcon>
                    <PillarTitle>
                      Pillar 3: Equity & Access
                    </PillarTitle>
                    <Typography>
                      Ensuring the benefits of AI are accessible to all learners by addressing the digital, pedagogical, and linguistic divides with inclusive technology and training.
                    </Typography>
                  </CardContent>
                </PillarCard>
              </Grid>
            </Grid>
          </Container>
        </PillarsSection>

        {/* Final CTA Section */}
        <FinalCTASection>
          <Container maxWidth="lg">
            <SectionTitle variant="h2">
              Get Your Step-by-Step Blueprint
            </SectionTitle>
            <SectionSubtitle>
              This white paper goes beyond theory. Gain access to actionable frameworks, educator-tested solutions, and the specific strategies you can implement to navigate the future of AI in education today.
            </SectionSubtitle>
            <CTAButton onClick={handleDownload}>
              Get the Actionable Blueprint
            </CTAButton>
          </Container>
        </FinalCTASection>

        {/* Footer */}
        <FooterSection>
          <Container maxWidth="lg">
            <FooterGrid container spacing={5}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <img 
                    src="https://ees.pythaverse.net/thoughtcocreate/images/logo.png" 
                    alt="Pythaverse Logo" 
                    style={{ width: '150px', marginBottom: '20px' }}
                  />
                </Box>
                <FooterText>
                  A roadmap built from the ground up, harnessing the frontline experience of teachers to build practical, responsible solutions that are grounded in the reality of the classroom.
                </FooterText>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FooterTitle>Quick Links</FooterTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FooterLink href="https://ees.pythaverse.net/thoughtcocreate/about-us.html">About Us</FooterLink>
                  <FooterLink href="https://ees.pythaverse.net/thoughtcocreate/contact-us.html">Contact Us</FooterLink>
                  <FooterLink href="#">Privacy Policy</FooterLink>
                  <FooterLink href="#">Terms & Conditions</FooterLink>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FooterTitle>Contact Us</FooterTitle>
                <FooterText>
                  123 Education Lane<br />
                  Innovation City, 12345<br />
                  contact@pythaverse.net
                </FooterText>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FooterTitle>Follow Us</FooterTitle>
                <Box>
                  <SocialIcon href="#" aria-label="Facebook">
                    <i className="fab fa-facebook-f"></i>
                  </SocialIcon>
                  <SocialIcon href="#" aria-label="Twitter">
                    <i className="fab fa-twitter"></i>
                  </SocialIcon>
                  <SocialIcon href="#" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in"></i>
                  </SocialIcon>
                </Box>
              </Grid>
            </FooterGrid>
            
            <FooterBottom>
              <Typography variant="body2">
                &copy; 2025 Pythaverse ThoughtCoCreate. All Rights Reserved.
              </Typography>
            </FooterBottom>
          </Container>
        </FooterSection>
      </Box>
    </ThemeProvider>
    
    </>
  );
};

export default TCCWhitePaper;
